import { useNavigate, useLocation, Navigate } from "react-router-dom";
import useBreakPoinst from "@hooks/useBreakPoinst";
import { useTranslation } from "react-i18next";
import { useState, ReactElement, useEffect, useMemo } from "react";
import AirbnbMsool from "@assets/svg/AirbnbMsool.svg?react";
import BookingMsool from "@assets/svg/BookingMsool.svg?react";
import TravelMsool from "@assets/svg/TravelMsool.svg?react";
import BlueSuccess from "@assets/svg/BlueSuccess.svg?react";
import ActivationCard from "./components/Airbnb/ActivationCard";
import {
  Wrapper,
  Title1,
  StepIndicatorWrapper,
  StepIndicator,
} from "./components/Airbnb/styles/AirbnbActivation";
import AirbnbActivateModal from "./components/Airbnb/AirbnbActivateModal";
import AirbnLinkSuccess from "./components/Airbnb/AirbnLinkSuccess";
import NewChannelModal from "./components/Booking/NewChannelModal";
import FirstStepModal from "./components/Booking/FirstStepModal";
import ComparePropertiesModal from "./components/Booking/ComparePropertiesModal";
import BookingSuccess from "./components/Booking/BookingSuccess";
import AutomateReservationsModal from "./components/Automation/AutomateReservationsModal";
import { useOnboarding } from "../../services/useOnboarding";
import GathernConnectModal from "./components/Gathern/GathernConnectModal";
import AirbnbChooseProperties from "./components/Airbnb/AirbnbChooseProperties";
import { IconContainer, IconWrapper } from "./components/Zappier/styles";
import MsoolLogo from "@assets/svg/msool-icon.svg?react";
import ArrowSwap from "@assets/svg/ArrowSwap.svg?react";
import GatherinLogo from "@assets/svg/GatherInSoon.svg?react";

interface StepData {
  stepNumber: string;
  title: string;
  description: string | React.ReactNode;
  icon: ReactElement<React.SVGProps<SVGSVGElement>>;
}

const formatDescription = (
  text: string,
  breakAfter: number,
  isPhone: boolean
): React.ReactNode => {
  if (!isPhone) return text;
  const words = text.split(" ");
  if (words.length <= breakAfter) return text;
  return (
    <>
      {words.slice(0, breakAfter).join(" ")}
      <br />
      {words.slice(breakAfter).join(" ")}
    </>
  );
};

const OnBoardingJourny = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isPhone } = useBreakPoinst();
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [showAirbnbModal, setShowAirbnbModal] = useState(false);
  const [showChooseProperties, setShowChooseProperties] = useState(false);
  const [showLinkSuccess, setShowLinkSuccess] = useState(false);
  const [BookingModal, setBookingModals] = useState({
    connectChannels: false,
    firstStep: false,
    ComparePropertiesModal: false,
    ShowLinkSuccess: false,
  });
  const [AutomationModal, setAutomationModals] = useState({
    chooseMissions: false,
  });
  const [GathernModal, setGathernModal] = useState({
    open: false,
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (
      location.pathname === "/chooseChannel" &&
      searchParams.get("airbnbCallback") === "true" &&
      activeStep < 1
    ) {
      setShowChooseProperties(true);
      navigate("/chooseChannel", { replace: true });
    }
  }, [location.pathname, location.search, activeStep, navigate]);

  /* ----------------
      Api calls
  -------------------- */
  const { getCheckOnboardingQuery } = useOnboarding();
  const { data: checkOnboardingObj, refetch } = getCheckOnboardingQuery();

  useEffect(() => {
    if (checkOnboardingObj?.result) {
      if (checkOnboardingObj?.result?.isGathernOnboardingComplete) {
        setActiveStep(4);
      } else if (checkOnboardingObj?.result?.isAutomationOnboardingComplete) {
        setActiveStep(3);
      } else if (checkOnboardingObj?.result?.isBookingOnboardingComplete) {
        setActiveStep(2);
      } else if (checkOnboardingObj?.result?.isAirbnbOnboardingComplete) {
        setActiveStep(1);
      } else {
        setActiveStep(0);
      }
    }
  }, [checkOnboardingObj]);

  const data: StepData[] = [
    {
      stepNumber: t("onBoardingJourny.step1Number"),
      title: t("onBoardingJourny.step1Title"),
      description: formatDescription(
        t("onBoardingJourny.step1Description"),
        19,
        isPhone
      ),
      icon: (
        <AirbnbMsool width={isPhone ? 60 : 80} height={isPhone ? 60 : 80} />
      ),
    },
    {
      stepNumber: t("onBoardingJourny.step2Number"),
      title: t("onBoardingJourny.step2Title"),
      description: formatDescription(
        t("onBoardingJourny.step2Description"),
        10,
        isPhone
      ),
      icon: (
        <BookingMsool width={isPhone ? 60 : 80} height={isPhone ? 60 : 80} />
      ),
    },
    {
      stepNumber: t("onBoardingJourny.step3Number"),
      title: t("onBoardingJourny.step3Title"),
      description: formatDescription(
        t("onBoardingJourny.step3Description"),
        12,
        isPhone
      ),
      icon: (
        <TravelMsool width={isPhone ? 60 : 80} height={isPhone ? 60 : 80} />
      ),
    },
    {
      stepNumber: t("onBoardingJourny.step4Number"),
      title: t("onBoardingJourny.step4Title"),
      description: formatDescription(
        t("onBoardingJourny.step4Description"),
        12,
        isPhone
      ),
      icon: (
        <IconContainer $isPhone={isPhone}>
          <IconWrapper $isPhone={isPhone}>
            <MsoolLogo width={isPhone ? 20 : 40} height={isPhone ? 20 : 40} />
            <ArrowSwap width={isPhone ? 8 : 16} height={isPhone ? 8 : 16} />
            <GatherinLogo
              width={isPhone ? 20 : 40}
              height={isPhone ? 20 : 40}
            />
          </IconWrapper>
        </IconContainer>
      ),
    },
  ];

  const currentActiveIndex = useMemo(() => {
    let current = 0;
    if (checkOnboardingObj?.result?.isAirbnbOnboardingComplete) {
      current = current + 1;
    }
    if (checkOnboardingObj?.result?.isBookingOnboardingComplete) {
      current = current + 1;
    }
    if (checkOnboardingObj?.result?.isAutomationOnboardingComplete) {
      current = current + 1;
    }
    if (checkOnboardingObj?.result?.isGathernOnboardingComplete) {
      current = current + 1;
    }
    return current;
  }, [JSON.stringify(checkOnboardingObj)]);

  const handleCardButton = (index: number) => {
    if (index === 0) {
      setShowAirbnbModal(true);
    }
    if (index === 1) {
      setBookingModals((prev) => ({ ...prev, connectChannels: true }));
    }
    if (index === 2) {
      setAutomationModals((prev) => ({ ...prev, chooseMissions: true }));
    }
    if (index === 3) {
      setGathernModal((prev) => ({ ...prev, open: true }));
    }
  };

  if (checkOnboardingObj?.result?.isOnboardingComplete) {
    return <Navigate to={`/dashboard`} />;
  }
  return (
    <Wrapper $isPhone={isPhone}>
      <Title1 $isPhone={isPhone}>
        {t("onBoardingJourny.startWithMasool")}
      </Title1>
      {/* AirModals */}
      <AirbnbActivateModal
        open={showAirbnbModal}
        onClose={() => setShowAirbnbModal(false)}
        handleNextModal={() => {
          setShowAirbnbModal(false);
          setShowChooseProperties(true);
        }}
        onRefetchOnboarding={refetch}
      />

      <AirbnbChooseProperties
        handleNextModal={() => {
          setShowChooseProperties(false);
          setShowLinkSuccess(true);
        }}
        onClose={() => {
          setShowChooseProperties(false);
        }}
        open={showChooseProperties}
        onRefetchOnboarding={refetch}
      />
      <AirbnLinkSuccess
        open={showLinkSuccess}
        onClose={() => {
          setShowLinkSuccess(false);
          setActiveStep(1);
        }}
      />
      {/* BookingModals */}
      {BookingModal.connectChannels && (
        <NewChannelModal
          onCLose={() => {
            setBookingModals((prev) => ({ ...prev, connectChannels: false }));
          }}
          handleNextModal={() =>
            setBookingModals((prev) => ({ ...prev, firstStep: true }))
          }
        />
      )}

      {BookingModal.firstStep && (
        <FirstStepModal
          open={BookingModal.firstStep}
          onClose={() =>
            setBookingModals((prev) => ({ ...prev, firstStep: false }))
          }
          handleNextModal={() =>
            setBookingModals((prev) => ({
              ...prev,
              ComparePropertiesModal: true,
            }))
          }
        />
      )}

      {BookingModal.ComparePropertiesModal && (
        <ComparePropertiesModal
          handleNextModal={() =>
            setBookingModals((prev) => ({
              ...prev,
              ShowLinkSuccess: true,
            }))
          }
          open={BookingModal.ComparePropertiesModal}
          onClose={() => {
            setBookingModals((prev) => ({
              ...prev,
              ComparePropertiesModal: false,
            }));
          }}
          onRefetchOnboarding={refetch}
        />
      )}
      {BookingModal.ShowLinkSuccess && (
        <BookingSuccess
          open={BookingModal.ShowLinkSuccess}
          onClose={() => {
            setBookingModals((prev) => ({
              ...prev,
              ShowLinkSuccess: false,
            }));
            setActiveStep(2);
          }}
        />
      )}
      {/* Automation Modals */}
      <AutomateReservationsModal
        open={AutomationModal.chooseMissions}
        onClose={() => {
          setAutomationModals((prev) => ({
            ...prev,
            chooseMissions: false,
          }));
        }}
        handleNextModal={() => {
          setAutomationModals((prev) => ({
            ...prev,
            chooseMissions: false,
          }));
          setActiveStep(3);
        }}
        onRefetchOnboarding={refetch}
      />
      {/* Gathern Connect Modal */}
      <GathernConnectModal
        open={GathernModal.open}
        onClose={() => {
          setGathernModal((prev) => ({ ...prev, open: false }));
        }}
        onSuccess={() => {
          setGathernModal((prev) => ({ ...prev, open: false }));
          setActiveStep(4);
          refetch();
        }}
      />

      <StepIndicatorWrapper $isPhone={isPhone}>
        {data?.map((_, index) => {
          return (
            <>
              <StepIndicator
                key={index}
                $isPhone={isPhone}
                $isActive={index < currentActiveIndex}
              />
              {index + 1 <= currentActiveIndex && (
                <BlueSuccess
                  style={{
                    width: "90px",
                    height: "90px",
                    display: "flex",
                    justifyContent: "start",
                    alignItems: "start",
                    translate: language === "ar" ? "10px 0" : "-10px 0",
                  }}
                />
              )}
            </>
          );
        })}
      </StepIndicatorWrapper>

      {data?.map((item, index) => {
        let checked = false;
        switch (index) {
          case 0:
            checked = checkOnboardingObj?.result?.isAirbnbOnboardingComplete;
            break;
          case 1:
            checked = checkOnboardingObj?.result?.isBookingOnboardingComplete;
            break;
          case 2:
            checked =
              checkOnboardingObj?.result?.isAutomationOnboardingComplete;
            break;
          case 3:
            checked = checkOnboardingObj?.result?.isGathernOnboardingComplete;
            break;

          default:
            break;
        }
        return (
          <ActivationCard
            activate={() => handleCardButton(index)}
            key={index}
            icon={item?.icon}
            title={item?.title}
            description={item?.description}
            step={item?.stepNumber}
            checked={checked}
            isPhone={isPhone}
            isDisabled={
              index == 0
                ? false
                : !checkOnboardingObj?.result?.isAirbnbOnboardingComplete
            }
          />
        );
      })}
    </Wrapper>
  );
};

export default OnBoardingJourny;
